import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { getOrders, getMenuItems, getRestaurant, createRestaurant, getTemplates, getRestaurants, updateOrderStatus } from '../services/api';
import ServiceRequestsPanel from '../components/Manager/ServiceRequestsPanel';
import { formatDateTime } from '../utils/formatDate';
import PriceTag from '../components/ui/PriceTag';
import AppShell from '../components/layout/AppShell';
import PageHeader from '../components/layout/PageHeader';
import KpiCard from '../components/ui/KpiCard';
import { ReceiptLong, Hourglass, RestaurantMenu } from '../components/ui/Icons';
import { toast } from '../lib/toast';
import './ManagerDashboard.css';

/* Statuses supported by the order status API */
const ORDER_STATUS_OPTIONS = ['pending', 'preparing', 'ready', 'completed'];

/* ── Inline SVG icon components ────────────────────────────── */
const Icons = {
  orders: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="18" rx="2"/><path d="M8 7h8"/><path d="M8 11h8"/><path d="M8 15h4"/>
    </svg>
  ),
  pending: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  menu: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/>
    </svg>
  ),
  manageMenu: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
    </svg>
  ),
  kitchen: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v3"/><line x1="12" y1="12" x2="12" y2="16"/>
    </svg>
  ),
  orderList: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
    </svg>
  ),
  qrCode: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="8" height="8" rx="1"/><rect x="14" y="2" width="8" height="8" rx="1"/><rect x="2" y="14" width="8" height="8" rx="1"/><path d="M14 14h2v2h-2z"/><path d="M20 14h2v2h-2z"/><path d="M14 20h2v2h-2z"/><path d="M20 20h2v2h-2z"/><path d="M17 17h2v2h-2z"/>
    </svg>
  ),
  info: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  ),
  theme: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="13.5" cy="6.5" r="2.5"/><circle cx="6.5" cy="12" r="2.5"/><circle cx="17.5" cy="12" r="2.5"/><circle cx="13.5" cy="17.5" r="2.5"/><circle cx="12" cy="12" r="9"/>
    </svg>
  ),
  analytics: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  ),
  billing: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/><line x1="6" y1="15" x2="10" y2="15"/>
    </svg>
  ),
  chevronRight: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  ),
  table: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/>
    </svg>
  ),
  restaurant: (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/>
    </svg>
  ),
  plus: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  ),
};

const ManagerDashboard = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? 'ar-SA' : i18n.language === 'es' ? 'es-ES' : 'en-US';
  const { user, restaurants, isManager, isVerified, setRestaurants: setRestaurantsContext } = useAuth();
  const [orders, setOrders] = useState([]);
  const [restaurantCurrency, setRestaurantCurrency] = useState(null);
  const [stats, setStats] = useState({ orders: 0, menuItems: 0, pendingOrders: 0 });
  const [loading, setLoading] = useState(true);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const [showNewRestaurant, setShowNewRestaurant] = useState(false);
  const [newRestaurantName, setNewRestaurantName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templates, setTemplates] = useState([]);
  const [creating, setCreating] = useState(false);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [resultDialogMessage, setResultDialogMessage] = useState('');

  useEffect(() => {
    if (isManager()) {
      if (restaurants.length > 0) {
        fetchDashboardData();
      } else {
        setLoading(false);
        fetchTemplates();
      }
    }
  }, [isManager, restaurants]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const restaurantId = restaurants[0].id;
      
      const [ordersRes, pendingRes, menuRes, restaurantRes] = await Promise.all([
        getOrders(restaurantId),
        getOrders(restaurantId, { status: 'pending', limit: 1 }),
        getMenuItems(restaurantId),
        getRestaurant(restaurantId).catch(() => ({ data: null }))
      ]);

      const curr = restaurantRes?.data?.currency;
      setRestaurantCurrency(curr && typeof curr === 'object' ? curr : null);

      const ordersList = ordersRes.data?.orders ?? ordersRes.data ?? [];
      setOrders(Array.isArray(ordersList) ? ordersList : []);

      const totalOrders = ordersRes.data?.total ?? (Array.isArray(ordersList) ? ordersList.length : 0);
      const pendingOrders = pendingRes.data?.total ?? 0;
      
      setStats({
        orders: totalOrders,
        menuItems: menuRes.data?.items?.length ?? 0,
        pendingOrders
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isManager()) {
    return (
      <div className="mgr-dashboard">
        <div className="mgr-error-card">
          <div className="mgr-error-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
            </svg>
          </div>
          <h2>{t('dashboard.manager.accessDenied.title')}</h2>
          <p>{t('dashboard.manager.accessDenied.message')}</p>
        </div>
      </div>
    );
  }

  // Unverified managers
  if (isManager() && !isVerified()) {
    return (
      <div className="mgr-dashboard">
        <div className="mgr-pending-card">
          <div className="mgr-pending-badge">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <h2>{t('dashboard.manager.pendingVerification.title')}</h2>
          <p className="mgr-pending-lead">{t('dashboard.manager.pendingVerification.subtitle')}</p>
          <ul className="mgr-pending-features">
            <li>
              <span className="mgr-feature-check">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </span>
              {t('dashboard.manager.pendingVerification.feature1')}
            </li>
            <li>
              <span className="mgr-feature-check">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </span>
              {t('dashboard.manager.pendingVerification.feature2')}
            </li>
            <li>
              <span className="mgr-feature-check">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </span>
              {t('dashboard.manager.pendingVerification.feature3')}
            </li>
          </ul>
          <p className="mgr-pending-detail">{t('dashboard.manager.pendingVerification.detail')}</p>
          <Link to="/menu/9?mode=demo" className="mgr-btn mgr-btn-primary mgr-btn-lg">
            {t('dashboard.manager.pendingVerification.tryDemo')}
            {Icons.chevronRight}
          </Link>
        </div>
      </div>
    );
  }

  const fetchTemplates = async () => {
    try {
      const response = await getTemplates();
      if (response && response.data) {
        setTemplates(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    }
  };

  const handleCreateRestaurant = async (e) => {
    e.preventDefault();
    if (!newRestaurantName.trim()) return;

    try {
      setCreating(true);
      const restaurantData = {
        name: newRestaurantName.trim(),
        templateId: selectedTemplate || null
      };
      
      const createRes = await createRestaurant(restaurantData);

      const restaurantsRes = await getRestaurants();
      setRestaurantsContext(restaurantsRes.data);

      setNewRestaurantName('');
      setSelectedTemplate('');
      setShowNewRestaurant(false);

      const slug = createRes?.data?.slug;
      const slugMsg = slug
        ? `\n${t('dashboard.admin.newRestaurant.slugLink', { url: `${window.location.origin}/menu/${slug}` })}`
        : '';
      setResultDialogMessage(t('dashboard.admin.newRestaurant.successMessage') + slugMsg);
      setResultDialogOpen(true);
    } catch (error) {
      setResultDialogMessage(error.response?.data?.error || t('api.errors.createRestaurant'));
      setResultDialogOpen(true);
    } finally {
      setCreating(false);
    }
  };

  if (restaurants.length === 0) {
    return (
      <div className="mgr-dashboard fade-in">
        {/* Greeting */}
        <div className="mgr-greeting">
          <div>
            <h1 className="mgr-greeting-title">{t('dashboard.manager.title')}</h1>
            <p className="mgr-greeting-sub">{t('dashboard.manager.welcome', { name: user?.name })}</p>
          </div>
        </div>

        {/* Create restaurant card */}
        <div className="mgr-card mgr-create-card">
          <div className="mgr-create-header">
            <div className="mgr-create-icon">{Icons.restaurant}</div>
            <div>
              <h2>{t('dashboard.manager.createRestaurant.title')}</h2>
              <p>{t('dashboard.manager.createRestaurant.subtitle')}</p>
            </div>
          </div>

          {!showNewRestaurant ? (
            <button
              className="mgr-btn mgr-btn-primary mgr-btn-lg"
              onClick={() => {
                setShowNewRestaurant(true);
                if (templates.length === 0) fetchTemplates();
              }}
            >
              {Icons.plus}
              {t('dashboard.manager.createRestaurant.button')}
            </button>
          ) : (
            <form className="mgr-create-form" onSubmit={handleCreateRestaurant}>
              <div className="mgr-form-group">
                <label>{t('dashboard.admin.newRestaurant.nameLabel')}</label>
                <input
                  type="text"
                  value={newRestaurantName}
                  onChange={(e) => setNewRestaurantName(e.target.value)}
                  placeholder={t('dashboard.admin.newRestaurant.namePlaceholder')}
                  autoFocus
                  required
                  disabled={creating}
                />
              </div>

              <div className="mgr-form-group">
                <label>{t('dashboard.admin.newRestaurant.templateLabel')}</label>
                <select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  disabled={creating}
                >
                  <option value="">{t('dashboard.admin.newRestaurant.noTemplate')}</option>
                  {templates && templates.length > 0 ? (
                    templates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.itemCount || template.items?.length || 0} {t('common.items')})
                      </option>
                    ))
                  ) : (
                    <option disabled>{t('dashboard.admin.newRestaurant.loadingTemplates')}</option>
                  )}
                </select>
                <span className="mgr-form-hint">
                  {t('dashboard.admin.newRestaurant.templateHint')}
                </span>
              </div>

              <div className="mgr-form-actions">
                <button type="submit" className="mgr-btn mgr-btn-primary" disabled={creating}>
                  {creating ? (
                    <><span className="mgr-spinner" /> {t('dashboard.admin.newRestaurant.creating')}</>
                  ) : (
                    t('dashboard.admin.newRestaurant.createButton')
                  )}
                </button>
                <button
                  type="button"
                  className="mgr-btn mgr-btn-ghost"
                  onClick={() => {
                    setShowNewRestaurant(false);
                    setNewRestaurantName('');
                    setSelectedTemplate('');
                  }}
                  disabled={creating}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  const restaurant = restaurants[0];

  const getStatusColor = (status) => {
    const map = { pending: 'warning', preparing: 'info', ready: 'success', completed: 'success', cancelled: 'error' };
    return map[status] || 'default';
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return t('dashboard.manager.justNow');
    if (mins === 1) return t('dashboard.manager.oneMinAgo');
    if (mins < 60) return t('dashboard.manager.minsAgo', { count: mins });
    if (hours === 1) return t('dashboard.manager.oneHourAgo');
    if (hours < 24) return t('dashboard.manager.hoursAgo', { count: hours });
    if (days === 1) return t('dashboard.manager.oneDayAgo');
    return t('dashboard.manager.daysAgo', { count: days });
  };

  const handleStatusChange = async (orderId, newStatus) => {
    const order = orders.find((o) => o.id === orderId);
    if (!order || order.status === newStatus) return;
    const previousStatus = order.status;
    setUpdatingOrderId(orderId);
    setOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o))
    );
    try {
      await updateOrderStatus(orderId, newStatus);
      toast.success(t('orders.statusUpdated', { defaultValue: 'Order status updated' }));
    } catch (err) {
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status: previousStatus } : o))
      );
      toast.error(t('orders.errors.statusUpdateFailed', { defaultValue: 'Failed to update status' }));
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const quickActions = [
    { to: `/admin?restaurant=${restaurant.id}`, label: t('dashboard.manager.manageMenu'), icon: Icons.manageMenu, variant: 'primary' },
    { to: `/kitchen/${restaurant.id}`, label: t('kitchen.title'), icon: Icons.kitchen, external: true },
    { to: `/admin/orders?restaurant=${restaurant.id}`, label: t('dashboard.manager.viewOrders'), icon: Icons.orderList },
    { to: `/admin/qr-codes?restaurant=${restaurant.id}`, label: t('dashboard.manager.generateQRCodes'), icon: Icons.qrCode },
    { to: '/manager/restaurant-info', label: t('dashboard.manager.restaurantInfo'), icon: Icons.info },
    { to: `/manager/restaurant/${restaurant.id}/theme`, label: t('dashboard.menuTheme', 'Menu Theme'), icon: Icons.theme },
    { to: `/manager/restaurant/${restaurant.id}/analytics`, label: t('dashboard.manager.analytics', 'Analytics'), icon: Icons.analytics },
    { to: '/subscription', label: t('subscription.dashboardTile', 'Subscription'), icon: Icons.billing },
  ];

  return (
    <AppShell maxWidth={false}>
      <PageHeader
        title={t('dashboard.manager.title')}
        subtitle={t('dashboard.manager.welcome', { name: user?.name })}
        action={
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              padding: '8px 16px',
              borderRadius: 9999,
              backgroundColor: 'success.light',
              color: '#FFFFFF',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#FFFFFF' }} />
            {restaurant.name}
          </Box>
        }
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 2.5,
          mb: 4,
        }}
      >
        <KpiCard
          icon={<ReceiptLong size={22} />}
          iconTone="primary"
          label={t('dashboard.manager.stats.totalOrders')}
          value={stats.orders}
        />
        <KpiCard
          icon={<Hourglass size={22} />}
          iconTone="secondary"
          label={t('dashboard.manager.stats.pendingOrders')}
          value={stats.pendingOrders}
        />
        <KpiCard
          icon={<RestaurantMenu size={22} />}
          iconTone="info"
          label={t('dashboard.manager.stats.menuItems')}
          value={stats.menuItems}
        />
      </Box>

      <div className="mgr-dashboard">


      {/* ── Two-column layout: Actions & Recent Orders ── */}
      <div className="mgr-columns">
        {/* Quick actions */}
        <div className="mgr-card">
          <div className="mgr-card-header">
            <h2>{t('dashboard.manager.quickActions')}</h2>
          </div>
          <div className="mgr-actions-grid">
            {quickActions.map((action) => (
              <Link
                key={action.to}
                to={action.to}
                className={`mgr-action-tile ${action.variant === 'primary' ? 'mgr-action-primary' : ''}`}
                {...(action.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                <span className="mgr-action-icon">{action.icon}</span>
                <span className="mgr-action-label">{action.label}</span>
                {Icons.chevronRight}
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Orders */}
        <div className="mgr-card">
          <div className="mgr-card-header">
            <h2>{t('dashboard.manager.recentOrders.title')}</h2>
            <Link to={`/admin/orders?restaurant=${restaurant.id}`} className="mgr-card-link">
              {t('dashboard.manager.recentOrders.viewAll')} {Icons.chevronRight}
            </Link>
          </div>

          {loading ? (
            <div className="mgr-loading">
              <span className="mgr-spinner" />
              {t('dashboard.manager.recentOrders.loading')}
            </div>
          ) : orders.length === 0 ? (
            <div className="mgr-empty">
              <div className="mgr-empty-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="18" rx="2"/><path d="M8 7h8"/><path d="M8 11h8"/><path d="M8 15h4"/>
                </svg>
              </div>
              <p>{t('dashboard.manager.recentOrders.empty')}</p>
            </div>
          ) : (
            <div className="mgr-orders-table">
              <div className="mgr-orders-head">
                <span>{t('dashboard.manager.recentOrders.table')}</span>
                <span>{t('dashboard.manager.recentOrders.status')}</span>
                <span>{t('dashboard.manager.recentOrders.time')}</span>
                <span className="mgr-text-right">{t('dashboard.manager.recentOrders.amount')}</span>
              </div>
              {orders.slice(0, 5).map(order => (
                <div
                  key={order.id}
                  className={`mgr-order-row ${updatingOrderId === order.id ? 'mgr-order-row--updating' : ''}`}
                >
                  <span className="mgr-order-table">
                    {order.order_type === 'car' ? (
                      <>🚗 {t('orders.carOrder')}</>
                    ) : (
                      <>
                        {Icons.table}
                        {t('dashboard.manager.recentOrders.tableWithNumber', { number: order.table_number })}
                      </>
                    )}
                  </span>
                  <span className="mgr-order-status-cell">
                    {updatingOrderId === order.id ? (
                      <span className="mgr-status-pill mgr-status-pill--loading">
                        <span className="mgr-status-spinner" aria-hidden />
                        {t(`orders.status.${order.status}`, { defaultValue: order.status })}
                      </span>
                    ) : (
                      <div className="mgr-status-boxes">
                        {ORDER_STATUS_OPTIONS.map((status) => (
                          <button
                            key={status}
                            type="button"
                            className={`mgr-status-box mgr-status-${getStatusColor(status)} ${order.status === status ? 'mgr-status-box--current' : ''}`}
                            onClick={() => handleStatusChange(order.id, status)}
                            disabled={order.status === status}
                            title={t(`orders.status.${status}`, { defaultValue: status })}
                          >
                            {t(`orders.status.${status}`, { defaultValue: status })}
                          </button>
                        ))}
                      </div>
                    )}
                  </span>
                  <span className="mgr-order-time" title={formatDateTime(order.created_at, locale)}>
                    {formatTimeAgo(order.created_at)}
                  </span>
                  <span className="mgr-order-amount">
                    <PriceTag amount={order.total_amount} currency={restaurantCurrency || 'USD'} locale={locale} size="sm" color="inherit" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Service Requests (full width) ────────────────────────────── */}
      <div className="mgr-card mgr-service-card">
        <ServiceRequestsPanel restaurantId={restaurant.id} />
      </div>

      {/* Result dialog */}
      <Dialog
        open={resultDialogOpen}
        onClose={() => setResultDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>{t('common.notice')}</DialogTitle>
        <DialogContent>
          <p style={{ margin: 0 }}>{resultDialogMessage}</p>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setResultDialogOpen(false)} variant="contained" color="primary">
            {t('common.ok')}
          </Button>
        </DialogActions>
      </Dialog>
      </div>
    </AppShell>
  );
};

export default ManagerDashboard;